import React, { useEffect, useMemo } from 'react';
import {
  Alert,
  Bullseye,
  Button,
  Checkbox,
  Content,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  Divider,
  Flex,
  FlexItem,
  Label,
  Spinner,
  Title,
  useWizardContext,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { ExclamationTriangleIcon, PencilAltIcon } from '@patternfly/react-icons';
import { formatPrice } from '../../../../utils/money';
import { useUserContext } from '../../../../contexts/user/UserContext';
import { useWallet } from '../../../../hooks/useWallet';
import PaymentSourceChooser from '../../../Organization/Billing/PaymentSourceChooser';
import type { PaymentSource } from '../../../../types/models/Payment';
import { StaffPaymentWaivedAlert } from '../Register/RecapStep';
import { vatRateLabel } from '../Register/types';
import type { ContactDraft } from '../Register/types';
import { TRANSFER_STEP_IDS } from './types';
import type { TransferCartItem } from './types';
import type { DomainOrderPreview } from '../../../../types/models';

interface TransferRecapStepProps {
  cart: TransferCartItem[];
  contact: ContactDraft;
  /** `true` = keep-contact transfer: no contact was captured, the existing registrant is preserved. */
  keepContact: boolean;
  preview: DomainOrderPreview | null;
  previewLoading: boolean;
  previewError: string | null;
  /** Triggers the exact-pricing transfer preview for the given cart FQDNs. */
  onRequestPreview: (domains: string[], type?: 'registration' | 'transfer') => void;
  agreementsAccepted: boolean;
  onAgreementsChange: (checked: boolean) => void;
  /** Payment source (prepaid balance / saved card / hosted checkout) — lifted to the wizard, sent as `payment`. */
  paymentSource: PaymentSource;
  onPaymentSourceChange: (source: PaymentSource) => void;
  /** Checkout only: also save the card used at Mollie for later one-click payments. */
  saveCard: boolean;
  onSaveCardChange: (checked: boolean) => void;
}

const SectionHeader: React.FunctionComponent<{ title: string; onEdit: () => void; editLabel: string }> = ({
  title,
  onEdit,
  editLabel,
}) => (
  <Flex justifyContent={{ default: 'justifyContentSpaceBetween' }} alignItems={{ default: 'alignItemsCenter' }}>
    <FlexItem>
      <Title headingLevel="h3" size="md">
        {title}
      </Title>
    </FlexItem>
    <FlexItem>
      <Button variant="link" isInline icon={<PencilAltIcon />} onClick={onEdit} aria-label={editLabel}>
        Edit
      </Button>
    </FlexItem>
  </Flex>
);

const TransferRecapStep: React.FunctionComponent<TransferRecapStepProps> = ({
  cart,
  contact,
  keepContact,
  preview,
  previewLoading,
  previewError,
  onRequestPreview,
  agreementsAccepted,
  onAgreementsChange,
  paymentSource,
  onPaymentSourceChange,
  saveCard,
  onSaveCardChange,
}) => {
  const { goToStepById } = useWizardContext();
  const user = useUserContext();
  const isStaff = user?.is_ancalagon_staff === true;
  // The wallet (balance + default saved card) is fetched here, on recap entry — like the preview —
  // never for a staff user, whose payment the backend waives (the `payment` object is ignored).
  const { wallet, defaultPaymentMethod, loading: walletLoading, error: walletError } = useWallet(!isStaff);
  const unsupported = preview?.items.filter((i) => !i.supported) ?? [];

  // This step body only mounts when the recap step is active (PatternFly unmounts inactive step
  // bodies), so requesting the preview here fetches exactly on entry — and re-fetches whenever the
  // cart changed since. `type: 'transfer'` is REQUIRED — without it the preview prices as a
  // registration and the total is wrong.
  const cartDomains = useMemo(() => cart.map((c) => c.domain), [cart]);
  useEffect(() => {
    if (cartDomains.length > 0) {
      onRequestPreview(cartDomains, 'transfer');
    }
  }, [cartDomains, onRequestPreview]);

  // Prefer the preview's per-item HT prices (the authoritative, client-specific values); fall back to
  // the live cart while the preview is (re)loading. Cart items are only ever added when supported, so
  // the fallback rows are supported.
  const rows =
    preview !== null && !previewLoading
      ? preview.items.map((i) => ({ domain: i.domain, tld: i.tld, price: i.unit_price_ht, supported: i.supported }))
      : cart.map((c) => ({ domain: c.domain, tld: c.tld, price: c.price, supported: true }));

  return (
    <Flex direction={{ default: 'column' }} spaceItems={{ default: 'spaceItemsLg' }}>
      <FlexItem>
        <Title headingLevel="h2" size="lg">
          Review &amp; pay
        </Title>
        <Content component="p">
          Review your transfer below. You can still go back and edit any step until you pay.
        </Content>
      </FlexItem>

      <FlexItem>
        <SectionHeader
          title="Domains"
          onEdit={() => goToStepById(TRANSFER_STEP_IDS.domains)}
          editLabel="Edit domains"
        />
        <Table aria-label="Transfer domains" variant="compact" className="pf-v6-u-mt-sm">
          <Thead>
            <Tr>
              <Th>Domain</Th>
              <Th>TLD</Th>
              <Th>Transfer price (excl. VAT)</Th>
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((r) => (
              <Tr key={r.domain}>
                <Td dataLabel="Domain">{r.domain}</Td>
                <Td dataLabel="TLD">.{r.tld}</Td>
                <Td dataLabel="Transfer price (excl. VAT)">
                  {r.supported && r.price !== null ? (
                    formatPrice(r.price)
                  ) : (
                    <Label color="orange" icon={<ExclamationTriangleIcon />}>
                      Not available
                    </Label>
                  )}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
        {unsupported.length > 0 && (
          <Alert variant="warning" isInline title="Some domains can't be transferred" className="pf-v6-u-mt-sm">
            {unsupported.map((i) => i.domain).join(', ')} {unsupported.length === 1 ? 'is' : 'are'} no longer offered
            and will be excluded. Go back to remove {unsupported.length === 1 ? 'it' : 'them'} before paying.
          </Alert>
        )}
      </FlexItem>

      <Divider />

      <FlexItem>
        <SectionHeader
          title="Registrant contact"
          onEdit={() => goToStepById(TRANSFER_STEP_IDS.contact)}
          editLabel="Edit contact"
        />
        {keepContact ? (
          <Content component="p" className="pf-v6-u-mt-sm">
            <strong>Kept from the current registrar.</strong> The domain&apos;s existing registrant contact is preserved
            as-is and will appear in the Manager automatically after the transfer completes.
          </Content>
        ) : (
          <DescriptionList isHorizontal isCompact className="pf-v6-u-mt-sm">
            <DescriptionListGroup>
              <DescriptionListTerm>Name</DescriptionListTerm>
              <DescriptionListDescription>
                {contact.first_name} {contact.last_name}
                {contact.organization ? ` — ${contact.organization}` : ''}
              </DescriptionListDescription>
            </DescriptionListGroup>
            <DescriptionListGroup>
              <DescriptionListTerm>Email</DescriptionListTerm>
              <DescriptionListDescription>{contact.email}</DescriptionListDescription>
            </DescriptionListGroup>
            <DescriptionListGroup>
              <DescriptionListTerm>Phone</DescriptionListTerm>
              <DescriptionListDescription>{contact.phone}</DescriptionListDescription>
            </DescriptionListGroup>
            <DescriptionListGroup>
              <DescriptionListTerm>Address</DescriptionListTerm>
              <DescriptionListDescription>
                {contact.address_line1}
                {contact.address_line2 ? `, ${contact.address_line2}` : ''}, {contact.zipcode} {contact.city},{' '}
                {contact.state}, {contact.countrycode}
              </DescriptionListDescription>
            </DescriptionListGroup>
          </DescriptionList>
        )}
      </FlexItem>

      <Divider />

      <FlexItem>
        <Alert variant="warning" isInline title="What to expect during the transfer">
          <Content component="p">
            A transfer can take several days to complete (up to ~40 days for some extensions). Keep the domain{' '}
            <strong>unlocked</strong> at your current registrar and don&apos;t cancel it there until the transfer
            finishes.
          </Content>
          <Content component="p" className="pf-v6-u-mt-sm">
            Any DNS zone or mailboxes you set up on this domain while the transfer is in progress are{' '}
            <strong>provisional</strong>: if the transfer doesn&apos;t complete, that configuration is removed (unless
            the domain was already verified with us).
          </Content>
        </Alert>
      </FlexItem>

      <FlexItem>
        <Title headingLevel="h3" size="md">
          Total
        </Title>
        {previewLoading ? (
          <Bullseye>
            <Spinner size="lg" aria-label="Computing the total" />
          </Bullseye>
        ) : previewError ? (
          <Alert variant="warning" isInline title="Couldn't compute the exact total" className="pf-v6-u-mt-sm">
            {previewError} The final amount will be shown at checkout.
          </Alert>
        ) : preview ? (
          <DescriptionList isHorizontal isCompact className="pf-v6-u-mt-sm">
            <DescriptionListGroup>
              <DescriptionListTerm>Subtotal (excl. VAT)</DescriptionListTerm>
              <DescriptionListDescription>{formatPrice(preview.amount_ht)}</DescriptionListDescription>
            </DescriptionListGroup>
            <DescriptionListGroup>
              <DescriptionListTerm>VAT</DescriptionListTerm>
              <DescriptionListDescription>{vatRateLabel(preview.vat_rate)}</DescriptionListDescription>
            </DescriptionListGroup>
            <DescriptionListGroup>
              <DescriptionListTerm>
                <strong>{isStaff ? 'Invoice total' : 'Total to pay'}</strong>
              </DescriptionListTerm>
              <DescriptionListDescription>
                <Content component="p">
                  <strong>{formatPrice(preview.amount_ttc)}</strong>
                </Content>
              </DescriptionListDescription>
            </DescriptionListGroup>
          </DescriptionList>
        ) : null}
        <StaffPaymentWaivedAlert />
      </FlexItem>

      {!isStaff && (
        <>
          <Divider />
          <FlexItem>
            <Title headingLevel="h3" size="md" className="pf-v6-u-mb-sm">
              Payment
            </Title>
            <PaymentSourceChooser
              idPrefix="transfer"
              value={paymentSource}
              onChange={onPaymentSourceChange}
              saveCard={saveCard}
              onSaveCardChange={onSaveCardChange}
              amountDue={preview && !previewLoading ? preview.amount_ttc : null}
              wallet={wallet}
              defaultPaymentMethod={defaultPaymentMethod}
              walletLoading={walletLoading}
              walletError={walletError}
            />
          </FlexItem>
        </>
      )}

      <Divider />

      <FlexItem>
        <Checkbox
          id="transfer-agreements"
          isChecked={agreementsAccepted}
          onChange={(_event, checked) => onAgreementsChange(checked)}
          label="I accept the transfer agreements and confirm I'm authorized to transfer these domains."
          isRequired
        />
      </FlexItem>
    </Flex>
  );
};

export default TransferRecapStep;
